import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";

export interface ApiState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
}

export interface UseApiResult<T> extends ApiState<T> {
  refetch: () => void;
}

function extractErrorMessage(err: unknown): string {
  if (typeof err === "object" && err !== null) {
    const asAxios = err as {
      response?: {
        data?:
          | string
          | {
              message?: string;
              error?: string;
              detail?: string;
              title?: string;
            };
      };
      message?: string;
    };
    const responseData = asAxios.response?.data;
    if (typeof responseData === "string" && responseData.trim()) return responseData;
    if (typeof responseData === "object" && responseData !== null) {
      const message =
        responseData.message ??
        responseData.error ??
        responseData.detail ??
        responseData.title;
      if (typeof message === "string" && message.trim()) return message;
    }
    return asAxios.message ?? "An unexpected error occurred";
  }
  return "An unexpected error occurred";
}

/**
 * Fetches data immediately and re-fetches whenever `deps` change or `refetch` is called.
 * In-flight requests are automatically cancelled on dep change or unmount.
 * Pass `skip: true` to suppress the fetch entirely (e.g. waiting for a user action).
 */
export function useApi<T>(
  fetcher: (signal: AbortSignal) => Promise<T>,
  deps: unknown[] = [],
  options: { skip?: boolean } = {}
): UseApiResult<T> {
  const [state, setState] = useState<ApiState<T>>({ data: null, loading: !options.skip, error: null });
  const [tick, setTick] = useState(0);

  useEffect(() => {
    if (options.skip) return;

    const controller = new AbortController();
    setState((prev) => ({ ...prev, loading: true, error: null }));

    fetcher(controller.signal)
      .then((data) => setState({ data, loading: false, error: null }))
      .catch((err: unknown) => {
        if (axios.isCancel(err)) return;
        setState({ data: null, loading: false, error: extractErrorMessage(err) });
      });

    return () => controller.abort();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tick, options.skip, ...deps]);

  const refetch = useCallback(() => setTick((t) => t + 1), []);

  return { ...state, refetch };
}

/**
 * Like `useApi` but does NOT fetch on mount.
 * Call `execute(fetcher)` to trigger the request manually.
 * Returns `null` on cancellation; check `error` state for failures.
 */
export function useLazyApi<T>(): {
  execute: (fetcher: (signal: AbortSignal) => Promise<T>) => Promise<T | null>;
  loading: boolean;
  error: string | null;
} {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  const execute = useCallback(async (fetcher: (signal: AbortSignal) => Promise<T>): Promise<T | null> => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;

    setLoading(true);
    setError(null);

    try {
      const data = await fetcher(controller.signal);
      if (abortRef.current === controller) setLoading(false);
      return controller.signal.aborted ? null : data;
    } catch (err: unknown) {
      if (abortRef.current === controller) {
        if (!axios.isCancel(err) && !controller.signal.aborted) setError(extractErrorMessage(err));
        setLoading(false);
      }
      return null;
    }
  }, []);

  return { execute, loading, error };
}
